/* eslint-disable no-console */
import { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { crearCreditoConCuotas, generarCuotas } from "../../utils/creditos";
import { getLocale } from "../../utils/getLocale";
import styles from "./Creditos.module.scss";

const TIPO_ACREEDOR_OPCIONES = [
  { value: "banco", label: "Banco" },
  { value: "privado", label: "Privado" },
  { value: "casa_matriz", label: "Casa matriz" },
  { value: "intercompany", label: "Intercompany" },
  { value: "socio", label: "Socio" },
];

const PERIODICIDAD_OPCIONES = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
  { value: "unica", label: "Pago único" },
];

const MONEDAS_POR_PAIS = {
  Chile: ["CLP", "USD"],
  Perú: ["PEN", "USD"],
};

// Formato de monto para input (sin decimales, miles según locale).
const formatMontoDisplay = (value, pais) => {
  if (value === "" || value == null) return "";
  if (typeof value === "string" && isNaN(Number(value))) return value;
  return Number(value).toLocaleString(getLocale(pais), { maximumFractionDigits: 0 });
};

const parseMontoInput = (displayValue) => {
  const clean = displayValue.replace(/\D/g, "");
  if (!clean) return "";
  const num = parseInt(clean, 10);
  return isNaN(num) ? "" : num;
};

const CrearCreditoModal = ({ isOpen, onClose, paisDefault, userId, onSuccess }) => {
  const initial = useMemo(
    () => ({
      nombre: "",
      pais: paisDefault || "Chile",
      tipo_acreedor: "banco",
      acreedor_nombre: "",
      monto_original: "",
      moneda: paisDefault === "Perú" ? "PEN" : "CLP",
      tasa_interes: "",
      tipo_tasa: "",
      num_cuotas: "",
      periodicidad: "mensual",
      dia_pago: "",
      fecha_primera_cuota: "",
      monto_cuota_fijo: "",
      cuotas_gracia: 0,
      pago_inicial: "",
      fecha_desembolso: "",
      notas: "",
    }),
    [paisDefault]
  );

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) setForm(initial);
  }, [isOpen, initial]);

  const set = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

  const monedasDisponibles = MONEDAS_POR_PAIS[form.pais] || ["CLP", "USD"];

  // Preview del schedule (primeras cuotas) para que el usuario vea qué se va a generar.
  const previewCuotas = useMemo(() => {
    if (!form.num_cuotas || !form.fecha_primera_cuota) return [];
    try {
      return generarCuotas({
        num_cuotas: Number(form.num_cuotas),
        periodicidad: form.periodicidad,
        dia_pago: form.dia_pago ? Number(form.dia_pago) : null,
        fecha_primera_cuota: form.fecha_primera_cuota,
        monto_cuota_fijo: form.monto_cuota_fijo === "" ? null : Number(form.monto_cuota_fijo),
        monto_original: Number(form.monto_original) || 0,
        cuotas_gracia: Number(form.cuotas_gracia) || 0,
      });
    } catch {
      return [];
    }
  }, [
    form.num_cuotas,
    form.periodicidad,
    form.dia_pago,
    form.fecha_primera_cuota,
    form.monto_cuota_fijo,
    form.monto_original,
    form.cuotas_gracia,
  ]);

  const validar = () => {
    if (!form.nombre.trim()) return "El nombre es requerido";
    if (!form.acreedor_nombre.trim()) return "El acreedor es requerido";
    if (!form.fecha_desembolso) return "La fecha de inicio / desembolso es requerida";
    if (!Number(form.monto_original) || Number(form.monto_original) <= 0) return "El monto original debe ser mayor a 0";
    if (!form.moneda) return "La moneda es requerida";
    if (!Number(form.num_cuotas) || Number(form.num_cuotas) <= 0) return "El número de cuotas debe ser mayor a 0";
    if (!form.fecha_primera_cuota) return "La fecha de la primera cuota es requerida";
    return null;
  };

  const handleGuardar = async () => {
    const error = validar();
    if (error) {
      toast.error(error);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        pais: form.pais,
        nombre: form.nombre.trim(),
        tipo_acreedor: form.tipo_acreedor,
        acreedor_nombre: form.acreedor_nombre.trim(),
        monto_original: Number(form.monto_original),
        moneda: form.moneda,
        tasa_interes: form.tasa_interes === "" ? null : Number(form.tasa_interes),
        tipo_tasa: form.tipo_tasa || null,
        num_cuotas: Number(form.num_cuotas),
        periodicidad: form.periodicidad,
        dia_pago: form.dia_pago === "" ? null : Number(form.dia_pago),
        fecha_desembolso: form.fecha_desembolso,
        fecha_primera_cuota: form.fecha_primera_cuota,
        monto_cuota_fijo: form.monto_cuota_fijo === "" ? null : Number(form.monto_cuota_fijo),
        cuotas_gracia: Number(form.cuotas_gracia) || 0,
        pago_inicial: form.pago_inicial === "" ? 0 : Number(form.pago_inicial),
        notas: form.notas.trim() || null,
        created_by: userId || null,
      };
      await crearCreditoConCuotas(payload);
      toast.success("Crédito creado");
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error creando crédito:", err);
      toast.error("Error al crear el crédito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={loading ? undefined : onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Nuevo crédito</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* ── Datos generales ── */}
        <div className={styles.modalSection}>
          <div className={styles.modalSectionTitle}>Datos generales</div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol} style={{ flex: 2 }}>
              <label className={styles.fieldLabel}>Nombre *</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Santander Crédito 420021536244"
                value={form.nombre}
                onChange={(e) => set("nombre", e.target.value)}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>País</label>
              <select
                className="form-select form-select-sm"
                value={form.pais}
                onChange={(e) => {
                  const nuevoPais = e.target.value;
                  set("pais", nuevoPais);
                  const monedas = MONEDAS_POR_PAIS[nuevoPais] || ["CLP"];
                  if (!monedas.includes(form.moneda)) set("moneda", monedas[0]);
                }}
                disabled={loading}
              >
                <option value="Chile">Chile</option>
                <option value="Perú">Perú</option>
              </select>
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Tipo de acreedor</label>
              <select
                className="form-select form-select-sm"
                value={form.tipo_acreedor}
                onChange={(e) => set("tipo_acreedor", e.target.value)}
                disabled={loading}
              >
                {TIPO_ACREEDOR_OPCIONES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.fieldCol} style={{ flex: 2 }}>
              <label className={styles.fieldLabel}>Acreedor *</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Banco Santander"
                value={form.acreedor_nombre}
                onChange={(e) => set("acreedor_nombre", e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Fecha inicio / desembolso *</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={form.fecha_desembolso}
                onChange={(e) => set("fecha_desembolso", e.target.value)}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol} style={{ flex: 2 }} />
          </div>
        </div>

        {/* ── Condiciones financieras ── */}
        <div className={styles.modalSection}>
          <div className={styles.modalSectionTitle}>Condiciones financieras</div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Monto original *</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formatMontoDisplay(form.monto_original, form.pais)}
                onChange={(e) => set("monto_original", parseMontoInput(e.target.value))}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Moneda *</label>
              <select
                className="form-select form-select-sm"
                value={form.moneda}
                onChange={(e) => set("moneda", e.target.value)}
                disabled={loading}
              >
                {monedasDisponibles.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Tasa interés anual (%)</label>
              <input
                type="number"
                step="0.01"
                className="form-control form-control-sm"
                value={form.tasa_interes}
                onChange={(e) => set("tasa_interes", e.target.value)}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Tipo de tasa</label>
              <select
                className="form-select form-select-sm"
                value={form.tipo_tasa}
                onChange={(e) => set("tipo_tasa", e.target.value)}
                disabled={loading}
              >
                <option value="">—</option>
                <option value="fija">Fija</option>
                <option value="variable">Variable</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── Schedule de pagos ── */}
        <div className={styles.modalSection}>
          <div className={styles.modalSectionTitle}>Schedule de pagos</div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>N° cuotas *</label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={form.num_cuotas}
                onChange={(e) => set("num_cuotas", e.target.value)}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Periodicidad</label>
              <select
                className="form-select form-select-sm"
                value={form.periodicidad}
                onChange={(e) => set("periodicidad", e.target.value)}
                disabled={loading}
              >
                {PERIODICIDAD_OPCIONES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Día de pago</label>
              <input
                type="number"
                min="1"
                max="31"
                className="form-control form-control-sm"
                value={form.dia_pago}
                onChange={(e) => set("dia_pago", e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Fecha primera cuota *</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={form.fecha_primera_cuota}
                onChange={(e) => set("fecha_primera_cuota", e.target.value)}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Monto cuota fija</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Vacío = reparto parejo"
                value={formatMontoDisplay(form.monto_cuota_fijo, form.pais)}
                onChange={(e) => set("monto_cuota_fijo", parseMontoInput(e.target.value))}
                disabled={loading}
              />
            </div>
          </div>
          {previewCuotas.length > 0 && (
            <div style={{ fontSize: "0.74rem", color: "#6c757d", marginTop: 4 }}>
              Se generarán {previewCuotas.length} cuotas — primera {previewCuotas[0].fecha_vencimiento}, última{" "}
              {previewCuotas.at(-1).fecha_vencimiento}.
            </div>
          )}
        </div>

        {/* ── Opciones adicionales ── */}
        <div className={styles.modalSection}>
          <div className={styles.modalSectionTitle}>Opciones adicionales</div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Cuotas de gracia</label>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                value={form.cuotas_gracia}
                onChange={(e) => set("cuotas_gracia", e.target.value)}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Pago inicial</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formatMontoDisplay(form.pago_inicial, form.pais)}
                onChange={(e) => set("pago_inicial", parseMontoInput(e.target.value))}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol} />
          </div>
          <div>
            <label className={styles.fieldLabel}>Notas</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={form.notas}
              onChange={(e) => set("notas", e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={loading}>
          {loading ? "Creando..." : "Crear crédito"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CrearCreditoModal;
